import moment from "moment";
import { useEffect, useState } from "react";
import { Badge, Form, Table } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import PaginationComponent from "../../../components/Pagination/Pagination";
import FormService from "../../../core/service/FormService";
import { ALL_STATUSES, FREQUENCY_OPTIONS, STATUS_LABEL_KEYS } from "../../../core/constants/common";
import "./DataCollectionSummaryCard.scss";

const DEFAULT_PAGE_SIZE = 10;

function getSummaryTotals(rows) {
    return (rows || []).reduce((acc, row) => {
        acc.PENDING_APPROVAL += row.statusCount?.PENDING_APPROVAL ?? 0;
        acc.CORRECTION_PENDING += row.statusCount?.CORRECTION_PENDING ?? 0;
        acc.NOT_STARTED += row.statusCount?.NOT_STARTED ?? 0;
        return acc;
    }, { PENDING_APPROVAL: 0, CORRECTION_PENDING: 0, NOT_STARTED: 0 });
}

function getFrequencyTotals(rows) {
    return (rows || []).reduce((acc, row) => {
        if (row.status === 'PENDING_APPROVAL') acc.PENDING_APPROVAL += 1;
        if (row.status === 'CORRECTION_PENDING') acc.CORRECTION_PENDING += 1;
        if (row.status === 'NOT_STARTED') acc.NOT_STARTED += 1;
        return acc;
    }, { PENDING_APPROVAL: 0, CORRECTION_PENDING: 0, NOT_STARTED: 0 });
}

function getMonthsInRange(periodStart, periodEnd) {
    if (!periodStart || !periodEnd) return [];
    const months = [];
    const cursor = moment(periodStart).startOf('month');
    const end = moment(periodEnd).startOf('month');
    while (cursor.isSameOrBefore(end)) {
        months.push({ value: cursor.format('MM'), label: cursor.format('MMM YYYY') });
        cursor.add(1, 'month');
    }
    return months;
}

function extractTotalCount(res, fallbackRows) {
    return res.data?.totalElements
        ?? res.data?.totalCount
        ?? res.data?.total
        ?? parseFloat(res.headers?.["x-total-count"])
        ?? (fallbackRows?.length ?? 0);
}

const DataCollectionSummaryCard = ({ t, periodStart, periodEnd, onToggleDetails }) => {
    const [frequency, setFrequency] = useState('SUMMARY');
    const [summary, setSummary] = useState(null);
    const [summaryTotalCount, setSummaryTotalCount] = useState(0);
    const [summaryPage, setSummaryPage] = useState(1);
    const [summaryPageSize, setSummaryPageSize] = useState(DEFAULT_PAGE_SIZE);

    const [detailsRows, setDetailsRows] = useState([]);
    const [detailsTotalCount, setDetailsTotalCount] = useState(0);
    const [detailsPage, setDetailsPage] = useState(1);
    const [detailsPageSize, setDetailsPageSize] = useState(DEFAULT_PAGE_SIZE);

    const [selectedMonth, setSelectedMonth] = useState('ALL');
    const [selectedStatus, setSelectedStatus] = useState('ALL');

    useEffect(() => {
        onToggleDetails?.(true);
    }, []);

    useEffect(() => {
        setSummary(null);
        setSummaryTotalCount(0);
        setSummaryPage(1);
        setDetailsRows([]);
        setDetailsTotalCount(0);
        setDetailsPage(1);
    }, [periodStart, periodEnd]);

    useEffect(() => {
        setDetailsPage(1);
        setSelectedMonth('ALL');
        setSelectedStatus('ALL');
    }, [frequency, periodStart, periodEnd]);

    useEffect(() => {
        setDetailsPage(1);
    }, [selectedMonth, selectedStatus]);

    useEffect(() => {
        if (!periodStart || !periodEnd) return;
        if (frequency === 'SUMMARY') {
            FormService.getBranchSubmissionsSummary(periodStart, periodEnd, summaryPage - 1, summaryPageSize).then(res => {
                if (res.status !== 200) return;
                setSummary(res.data);
                setSummaryTotalCount(extractTotalCount(res, res.data?.sections?.SUMMARY));
            }).catch(error => {
                console.error('Error fetching branch submissions summary:', error);
            });
        }
    }, [periodStart, periodEnd, frequency, summaryPage, summaryPageSize]);

    useEffect(() => {
        if (!periodStart || !periodEnd || frequency === 'SUMMARY') return;
        const isMonthlyFreq = frequency === 'MONTHLY';
        const apiStatus = selectedStatus === 'NOT_STARTED' ? 'DRAFT' : selectedStatus;
        const status = isMonthlyFreq && selectedStatus !== 'ALL' ? apiStatus : ALL_STATUSES;
        const reportingPeriod = isMonthlyFreq && selectedMonth !== 'ALL' ? selectedMonth : null;
        FormService.getBranchSubmissionsByFrequencyAndStatus(
            periodStart, periodEnd, frequency, status, detailsPage - 1, detailsPageSize, reportingPeriod
        ).then(res => {
            if (res.status !== 200) return;
            const rows = res.data?.branchDetails || [];
            setDetailsRows(rows);
            setDetailsTotalCount(extractTotalCount(res, rows));
        }).catch(error => {
            console.error('Error fetching branch submissions by frequency and status:', error);
        });
    }, [periodStart, periodEnd, frequency, detailsPage, detailsPageSize, selectedMonth, selectedStatus]);

    const isSummary = frequency === 'SUMMARY';
    const isMonthly = frequency === 'MONTHLY';
    const summaryRows = summary?.sections?.SUMMARY || [];
    const monthOptions = getMonthsInRange(periodStart, periodEnd);
    const totals = isSummary ? getSummaryTotals(summaryRows) : getFrequencyTotals(detailsRows);

    const renderFrequencySelect = () => (
        <div className="data-collection-summary-card-frequency flex-grow-0 flex-shrink-0">
            <Form.Select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
            >
                {FREQUENCY_OPTIONS.map(({ key, labelKey }) => (
                    <option value={key} key={key}>{t(labelKey)}</option>
                ))}
            </Form.Select>
        </div>
    );

    const renderMonthlyFilters = () => (
        <>
            <div className="data-collection-summary-card-frequency flex-grow-0 flex-shrink-0">
                <Form.Select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                >
                    <option value="ALL">{t("ReportingPeriod.label")}</option>
                    {monthOptions.map(({ value, label }) => (
                        <option value={value} key={value}>{label}</option>
                    ))}
                </Form.Select>
            </div>
            <div className="data-collection-summary-card-frequency flex-grow-0 flex-shrink-0">
                <Form.Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                >
                    <option value="ALL">{t("Status.label")}</option>
                    {Object.keys(STATUS_LABEL_KEYS).map((statusKey) => (
                        <option value={statusKey} key={statusKey}>{t(STATUS_LABEL_KEYS[statusKey])}</option>
                    ))}
                </Form.Select>
            </div>
        </>
    );

    const renderStatusList = () => (
        <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
            <li className="d-flex justify-content-between align-items-center py-1">
                <span className="text-secondary">{t("PendingApproval.label")}</span>
                <span className="fw-bold">{totals.PENDING_APPROVAL}</span>
            </li>
            <li className="d-flex justify-content-between align-items-center py-1">
                <span className="text-secondary">{t("CorrectionPending.label")}</span>
                <span className="fw-bold">{totals.CORRECTION_PENDING}</span>
            </li>
            <li className="d-flex justify-content-between align-items-center py-1">
                <span className="text-secondary">{t("Notstarted.label")}</span>
                <span className="fw-bold">{totals.NOT_STARTED}</span>
            </li>
        </ul>
    );

    const renderSummaryDetailsTable = () => (
        <>
            <div className="card List_table-card01 ">
                <Table responsive className="data-collection-summary-card-details-table mb-0">
                    <thead className="top_LR">
                        <tr>
                            <th>{t("ParentEntity.label")}</th>
                            <th>{t("Total.label")}</th>
                            <th>{t("Completed.label")}</th>
                            <th>{t("PendingApproval.label")}</th>
                            <th>{t("CorrectionPending.label")}</th>
                            <th>{t("InProgress.label")}</th>
                            <th>{t("Notstarted.label")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summaryRows.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="text-center text-secondary">{t("Nodatafound.label")}</td>
                            </tr>
                        ) : (
                            summaryRows.map((row, index) => (
                                <tr key={row.branchId ?? index}>
                                    <td>{row.branchName}</td>
                                    <td>{row.total ?? 0}</td>
                                    <td className="text-Completed">{row.statusCount?.COMPLETED ?? 0}</td>
                                    <td className="text-Accepted">{row.statusCount?.PENDING_APPROVAL ?? 0}</td>
                                    <td>{row.statusCount?.CORRECTION_PENDING ?? 0}</td>
                                    <td className="text-High">{row.statusCount?.IN_PROGRESS ?? 0}</td>
                                    <td className="text-Critical">{row.statusCount?.NOT_STARTED ?? 0}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
                {summaryRows.length > 0 && (
                    <div className="p-2 data-collection-summary-card-pagination d-flex flex-wrap align-items-center justify-content-end gap-1">
                        <PaginationComponent
                            itemsTotalCount={summaryTotalCount}
                            itemsPerPage={summaryPageSize}
                            currentPage={summaryPage}
                            setCurrentPage={setSummaryPage}
                            setItemsPerPage={setSummaryPageSize}
                            pageClicked={(pageNumber, perPageCount) => setSummaryPage(perPageCount ? 1 : pageNumber)}
                            alwaysShown={true}
                        />
                    </div>
                )}
            </div>
        </>
    );

    const renderFrequencyDetailsTable = () => (
        <>
            <div className="card List_table-card01 ">
                <Table responsive className="data-collection-summary-card-details-table mb-0">
                    <thead className="top_LR">
                        <tr>
                            <th>{t("ParentEntity.label")}</th>
                            <th>{t("ChildEntity.label")}</th>
                            <th>{t("DataOwners.label")}</th>
                            <th>{t("Status.label")}</th>
                            <th>{t("ReportingPeriod.label")}</th>
                            <th>{t("Events.label")}</th>
                            <th>{t("LastModified.label")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {detailsRows.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="text-center text-secondary">{t("Nodatafound.label")}</td>
                            </tr>
                        ) : (
                            detailsRows.map((row, index) => (
                                <tr key={row.submissionId ?? index}>
                                    <td>{row.parentBranchName}</td>
                                    <td>{row.childBranchName}</td>
                                    <td>{row.dataOwner}</td>
                                    <td >
                                        <Badge bg={row.status === 'PENDING_APPROVAL' ? 'badge-Accepted' : row.status === 'COMPLETED' ? 'badge-Completed' : row.status === 'NOT_STARTED' ? 'badge-Critical' : row.status === 'IN_PROGRESS' ? 'badge-High' : 'secondary'}>{t(STATUS_LABEL_KEYS[row.status] || row.status)}</Badge>
                                    </td>
                                    <td>{row.reportingPeriod ? moment(row.reportingPeriod).format("MMM DD, YYYY") : ''}</td>
                                    <td>{row.assessmentName}</td>
                                    <td>{row.status !== 'NOT_STARTED' && row.lastModifiedDate ? moment(row.lastModifiedDate).format("MMM DD, YYYY") : ''}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
                {detailsRows.length > 0 && (
                    <div className="p-2 data-collection-summary-card-pagination d-flex flex-wrap align-items-center justify-content-end gap-1">
                        <PaginationComponent
                            itemsTotalCount={detailsTotalCount}
                            itemsPerPage={detailsPageSize}
                            currentPage={detailsPage}
                            setCurrentPage={setDetailsPage}
                            setItemsPerPage={setDetailsPageSize}
                            pageClicked={(pageNumber, perPageCount) => setDetailsPage(perPageCount ? 1 : pageNumber)}
                            alwaysShown={true}
                        />
                    </div>
                )}
            </div>
        </>
    );

    return (
        <div className="data-collection-summary-card d-flex flex-column w-100">
            <div className="data-collection-summary-card-filters d-flex flex-wrap">
                {renderFrequencySelect()}
                {isMonthly && renderMonthlyFilters()}
            </div>
            {/* {renderStatusList()} */}
            {isSummary ? renderSummaryDetailsTable() : renderFrequencyDetailsTable()}
        </div>
    );
};

export default withTranslation()(DataCollectionSummaryCard);
